import { useQuery } from '@tanstack/react-query';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useService,
} from '../../../../../hooks';
import { DimensionDatum } from './useFetchDimensionData';
import MedicalResultsService, {
  ResultHistory,
} from '../../../../../services/MedicalResultsService';
import { LabResultsConfigItem } from '../useLabResultsConfig';
import { buildResultHistoryFallbackFromV3 } from '../buildResultHistoryFallbackFromV3';
import { formatResultHistoryCodingForDisplay } from '../formatters';
import { shouldIncludeDocumentExtractionV2 } from '../interlacedExtractedResults';
import { createConcurrencyLimiter } from '../../../../../util/concurrencyLimiter';

const SERVICE = new MedicalResultsService();

// The Lab Results view eagerly mounts one section per config item and each
// section fans out one `/history` request per biomarker. Without a ceiling this
// fires hundreds of requests at once and drains the shared per-account
// fhir-search-service rate limit (surfacing as 500s). A module-level limiter
// bounds the in-flight `/history` requests across every section at once.
const MEDICAL_RESULT_HISTORY_CONCURRENCY = 6;
const historyRequestLimiter = createConcurrencyLimiter(
  MEDICAL_RESULT_HISTORY_CONCURRENCY,
);

export type UseFetchMedicalResultHistoriesArgs = {
  patientId: string;
  dimensionData?: DimensionDatum[];
  labResultsConfigItem: LabResultsConfigItem;
};

export type MedicalResultHistory = {
  medicalResultHistory: ResultHistory[];
} & DimensionDatum;

export function useFetchMedicalResultHistories({
  patientId,
  dimensionData,
  labResultsConfigItem,
}: UseFetchMedicalResultHistoriesArgs) {
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const [getHistory] = useService(SERVICE.getHistory);
  const shouldInterlaceExtractedResults = shouldIncludeDocumentExtractionV2({
    source: labResultsConfigItem.source,
    isDocumentExtractionV2Enabled: true,
  });
  // Mirror useFetchDimensionData: the standalone extracted-results section only
  // wants codings that aren't already mapped into the categorized lab sections.
  const excludeMappedCodings =
    labResultsConfigItem.source === 'documentExtractionV2' || undefined;

  const code = dimensionData?.[0]?.dimension.codes[0].key;
  const system = dimensionData?.[0]?.dimension.codes[0].system;

  return useQuery({
    queryKey: [
      'medical-results',
      patientId,
      'history',
      {
        projectId,
        cohortId,
        code,
        system,
        shouldInterlaceExtractedResults,
        excludeMappedCodings,
      },
      labResultsConfigItem.source,
    ],
    queryFn: async () => {
      return await Promise.all(
        dimensionData!.map(datum =>
          historyRequestLimiter(async () => {
            const response = await getHistory({
              patientId,
              project: projectId,
              code: datum.dimension.codes[0].key,
              system: datum.dimension.codes[0].system,
              cohort: cohortId,
              includeClinicOnly: true,
              source: labResultsConfigItem.source,
              includeDocumentExtractionV2: shouldInterlaceExtractedResults,
              excludeMappedCodings,
            });
            return response.data;
          }),
        ),
      );
    },
    enabled: dimensionData !== undefined,
    select: medicalResultHistories => {
      return dimensionData!.map((datum, index) => {
        const fromApi = medicalResultHistories[index].items;
        const fallback = buildResultHistoryFallbackFromV3(datum);
        const items =
          fromApi.length > 0
            ? fromApi
            : fallback.length > 0
            ? fallback
            : fromApi;

        return {
          ...datum,
          medicalResultHistory: formatResultHistoryCodingForDisplay(
            items,
            labResultsConfigItem.source === 'memberUpload',
          ),
        };
      });
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}
